/* eslint-disable react/no-unused-prop-types */
import cn from 'classnames';
import he from 'he';
import Link from 'next/link';
import React, { ReactNode, useMemo } from 'react';
import { Locale } from '@shared/libs';
import type { TMenu } from '@shared/types';
import { Int3 } from '@shared/ui';
import { SubmenuHeader } from '../submenu-header';
import styles from './submenu-level-2.module.css';

type TProps = {
  data: TMenu | null;
  open: boolean;
  isMobile: boolean;
  locale: Locale;
  closeSubMenu: () => void;
  closeAllMenu: () => void;
  ContentSlot: () => ReactNode | null;
  isPopularMenu?: boolean;
  isTablet?: boolean;
};

export const SubmenuLevel2 = ({
  data,
  open,
  isMobile,
  locale,
  closeSubMenu,
  closeAllMenu,
  ContentSlot,
  isPopularMenu = false,
  isTablet = false,
}: TProps) => {
  // Разделяем элементы на столбцы для популярного меню
  const { firstColumnItems, secondColumnItems, shouldUseTwoColumns } = useMemo(() => {
    if (!isPopularMenu || isTablet || !data?.children) {
      return { firstColumnItems: [], secondColumnItems: [], shouldUseTwoColumns: false };
    }

    // Рассчитываем максимальное количество элементов в одном столбце
    // При высоте элемента ~32px (24px + 8px margin) и максимальной высоте 300px
    const maxItemsInColumn = Math.floor(300 / 32);
    
    // Если все элементы помещаются в один столбец - используем один столбец
    if (data.children.length <= maxItemsInColumn) {
      return {
        firstColumnItems: data.children,
        secondColumnItems: [],
        shouldUseTwoColumns: false,
      };
    }
    
    // Если не помещаются - делим на два столбца
    return {
      firstColumnItems: data.children.slice(0, maxItemsInColumn),
      secondColumnItems: data.children.slice(maxItemsInColumn),
      shouldUseTwoColumns: true,
    };
  }, [data?.children, isPopularMenu, isTablet]);

  // Для обычного меню или мобильной версии используем старую логику
  if (!isPopularMenu || isTablet) {
    return (
      <>
        {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
        <div className={styles.backdrop} onClick={closeSubMenu} />
        <div
          className={cn(styles.submenuLevel2, {
            [styles.open]: open,
            [styles.mobile]: isMobile,
          })}
        >
          {isMobile && (
            <SubmenuHeader
              closeAllMenu={closeAllMenu}
              closeSubMenu={closeSubMenu}
              title={data?.title || ''}
              subURL={data?.url || '#'}
              locale={locale}
            />
          )}
          {data?.children?.map(({ id, title: titleItem, url }: TMenu) => (
            <Link key={id} href={url} className={styles.submenuLink} prefetch={false}>
              <Int3>{he.decode(titleItem)}</Int3>
            </Link>
          ))}
          {isMobile && <ContentSlot />}
        </div>
      </>
    );
  }

  // Логика для популярного меню в десктопной версии
  return (
    <>
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
      <div className={styles.backdrop} onClick={closeSubMenu} />
      <div
        className={cn(styles.submenuLevel2, styles.popularMenu, {
          [styles.open]: open,
          [styles.twoColumns]: shouldUseTwoColumns,
          [styles.mobile]: isMobile,
        })}
      >
        {isMobile && (
          <SubmenuHeader
            closeAllMenu={closeAllMenu}
            closeSubMenu={closeSubMenu}
            title={data?.title || ''}
            subURL={data?.url || '#'}
            locale={locale}
          />
        )}
        <div className={styles.popularMenuContent}>
          <div className={styles.column}>
            {firstColumnItems.map(({ id, title: titleItem, url }: TMenu) => (
              <Link key={id} href={url} className={styles.submenuLink} prefetch={false}>
                <Int3>{he.decode(titleItem)}</Int3>
              </Link>
            ))}
          </div>
          {shouldUseTwoColumns && (
            <div className={styles.column}>
              {secondColumnItems.map(({ id, title: titleItem, url }: TMenu) => (
                <Link key={id} href={url} className={styles.submenuLink} prefetch={false}>
                  <Int3>{he.decode(titleItem)}</Int3>
                </Link>
              ))}
            </div>
          )}
        </div>
        {isMobile && <ContentSlot />}
      </div>
    </>
  );
};
